import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, ShieldWarning, Warning, MagnifyingGlass, Lock, XCircle, CheckCircle, ArrowClockwise, CircleNotch, Pen } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  verified:                  { color: "#047857", icon: ShieldCheck, label: "Verified" },
  partially_verified:        { color: "#1D4ED8", icon: CheckCircle, label: "Partially verified" },
  accepted_by_reviewer:      { color: "#0E7490", icon: CheckCircle, label: "Accepted by reviewer" },
  conflict_identified:       { color: "#B45309", icon: Warning, label: "Conflict identified" },
  original_source_not_found: { color: "#64748B", icon: MagnifyingGlass, label: "Original source not found" },
  internal_document:         { color: "#334155", icon: Lock, label: "Internal document" },
  retracted_or_corrected:    { color: "#B91C1C", icon: XCircle, label: "Retracted or corrected" },
};

const STATUS_ORDER = ["verified", "partially_verified", "accepted_by_reviewer", "conflict_identified",
                       "original_source_not_found", "internal_document", "retracted_or_corrected"];

export default function CitationsPage({ embedded }) {
  const { productId, product } = useProduct();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [correction, setCorrection] = useState(null); // {citation, updated_fields, reason}
  const [generating, setGenerating] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [missingRecords, setMissingRecords] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/citations`, { params: { product_id: productId, status: statusFilter || undefined } });
      setRows(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load citations");
    } finally { setLoading(false); }
  }, [productId, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/evidence-experience/${productId}/review-queue`)
      .then((r) => setMissingRecords(r.data.health?.missing_citation_records || 0))
      .catch(() => setMissingRecords(0));
  }, [productId, generating, accepting, resolving]);

  const generateAll = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/citations/process-all`, { product_id: productId });
      const { created, by_status } = r.data;
      toast.success(`${created} citation record(s) created`, {
        description: Object.entries(by_status).map(([s, n]) => `${STATUS_META[s]?.label || s}: ${n}`).join(" · ") || "All assets already had citation records.",
        duration: 8000,
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Citation generation failed");
    } finally { setGenerating(false); }
  };

  const resolveSources = async () => {
    setResolving(true);
    try {
      const r = await axios.post(`${API}/citations/resolve-sources`, { product_id: productId });
      const { checked, resolved, enriched } = r.data;
      toast.success(`Checked ${checked} against your uploaded source documents`, {
        description: `${resolved} matched to PubMed/CT.gov and upgraded · ${enriched} enriched with document metadata (no external identifier found)`,
        duration: 9000,
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Source resolution failed");
    } finally { setResolving(false); }
  };

  const submitCorrection = async () => {
    const reason = (correction.reason || "").trim();
    if (reason.length < 5) return toast.error("Reason must be at least 5 characters");
    try {
      await axios.post(`${API}/citations/${correction.citation.id}/manual-correction`, {
        updated_fields: correction.updated_fields,
        manual_match_authority_id: correction.manual_match_authority_id || null,
        reason,
      });
      toast.success("Correction applied — audit trail updated");
      setCorrection(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Correction failed");
    }
  };

  const acceptAll = async () => {
    setAccepting(true);
    try {
      const r = await axios.post(`${API}/citations/accept-all`, { product_id: productId });
      toast.success(`${r.data.accepted} citations accepted under your reviewer sign-off`, {
        description: `Recorded as "Accepted by reviewer" with your name (${r.data.accepted_by}) in the audit trail · asset verification refreshed (${r.data.verification?.verified ?? 0} verified)`,
        duration: 9000,
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk acceptance failed");
    } finally { setAccepting(false); }
  };

  const counts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = rows.filter(r => r.status === s).length; return acc;
  }, {});

  return (
    <PageShell embedded={embedded} movedTo={{ label: "Evidence Review", to: "/console/evidence-review?tab=sources" }} pageId="citations" overline={`MAI Knowledge Hub™ · Reference Recovery · ${product?.name || ""}`}
      title="Citation Recovery & Validation">

      {missingRecords > 0 && (
        <div data-testid="missing-records-banner" className="border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 flex items-center justify-between gap-3">
          <span><b>{missingRecords}</b> evidence asset{missingRecords === 1 ? " has" : "s have"} no citation record yet — they won't appear in this list until records are created.</span>
          <Button data-testid="missing-records-generate-btn" onClick={generateAll} disabled={generating}
                  className="text-xs rounded-none bg-slate-900 hover:bg-slate-800 text-white shrink-0">
            {generating ? <CircleNotch size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
            <span className="ml-1.5">Generate citation records</span>
          </Button>
        </div>
      )}

      {/* Status counts */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="citations-counts">
        {STATUS_ORDER.map((s) => {
          const meta = STATUS_META[s];
          return (
            <button key={s} data-testid={`citations-status-btn-${s}`}
                    onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
                    className={`text-left border px-3 py-2.5 bg-white ${statusFilter === s ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"}`}>
              <div className="flex items-center gap-1 mb-1">
                <meta.icon size={11} weight="bold" style={{ color: meta.color }} />
                <span className="text-[9px] uppercase tracking-wider font-semibold text-slate-600">{meta.label}</span>
              </div>
              <p className="font-mono-data text-xl font-bold" style={{ color: meta.color }}>{counts[s] || 0}</p>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <p className="text-xs text-slate-500">
          {statusFilter ? `Filtered by: ${STATUS_META[statusFilter]?.label}` : `Showing all ${rows.length} citations`}
        </p>
        <Button data-testid="citations-generate-btn" onClick={generateAll} disabled={generating}
                className="text-xs rounded-none ml-auto bg-slate-900 hover:bg-slate-800 text-white">
          {generating ? <CircleNotch size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
          <span className="ml-1.5">{generating ? "Processing evidence…" : "Generate citation records"}</span>
        </Button>
        <Button data-testid="citations-resolve-btn" onClick={resolveSources} disabled={resolving}
                className="text-xs rounded-none bg-emerald-700 hover:bg-emerald-800 text-white">
          {resolving ? <CircleNotch size={12} className="animate-spin" /> : <MagnifyingGlass size={12} />}
          <span className="ml-1.5">{resolving ? "Reading source documents…" : "Resolve from source documents"}</span>
        </Button>
        <Button data-testid="citations-accept-all-btn" onClick={acceptAll} disabled={accepting}
                className="text-xs rounded-none bg-cyan-800 hover:bg-cyan-900 text-white">
          {accepting ? <CircleNotch size={12} className="animate-spin" /> : <CheckCircle size={12} />}
          <span className="ml-1.5">{accepting ? "Signing off…" : "Accept all (reviewer sign-off)"}</span>
        </Button>
        <Button data-testid="citations-refresh-btn" onClick={load} disabled={loading}
                variant="outline" className="text-xs rounded-none">
          {loading ? <CircleNotch size={12} className="animate-spin" /> : <ArrowClockwise size={12} />}
          <span className="ml-1.5">Refresh</span>
        </Button>
      </div>

      {rows.length === 0 && !loading && (
        <div data-testid="citations-empty" className="border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm text-slate-600">No citation records {statusFilter ? "match this filter" : "yet"}.</p>
          <p className="text-xs text-slate-400 mt-1 max-w-lg mx-auto">
            Your evidence assets exist, but their references haven't been traced back to original sources yet.
            Click <b>Generate citation records</b> above — MAI will extract the bibliographic details from each
            asset and attempt to match them against authoritative sources (DOI, PMID, trial registries).
          </p>
          <Button data-testid="citations-empty-generate-btn" onClick={generateAll} disabled={generating}
                  className="mt-4 text-xs rounded-none bg-slate-900 hover:bg-slate-800 text-white">
            {generating ? <CircleNotch size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
            <span className="ml-1.5">{generating ? "Processing evidence…" : "Generate citation records"}</span>
          </Button>
        </div>
      )}

      <div className="border border-slate-200 bg-white">
        {rows.map((c) => {
          const meta = STATUS_META[c.status] || STATUS_META.original_source_not_found;
          return (
            <div key={c.id} data-testid={`citation-row-${c.id}`} className="border-b last:border-b-0 border-slate-100 px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] px-2 py-0.5 border font-bold uppercase tracking-wider" style={{ color: meta.color, borderColor: meta.color }}>
                      <meta.icon size={9} className="inline-block mr-1" />{meta.label}
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono">{c.filename}</span>
                  </div>
                  <p className="text-xs font-semibold text-slate-800 truncate">{c.extracted?.title || <i className="text-slate-400">(no title extracted)</i>}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {(c.extracted?.authors || []).slice(0, 3).join(", ")}
                    {c.extracted?.year && ` · ${c.extracted.year}`}
                    {c.extracted?.journal && ` · ${c.extracted.journal}`}
                    {c.extracted?.doi && ` · DOI ${c.extracted.doi}`}
                    {c.extracted?.pmid && ` · PMID ${c.extracted.pmid}`}
                    {c.extracted?.trial_registration_id && ` · ${c.extracted.trial_registration_id}`}
                    {c.extracted?.hta_decision_number && ` · HTA ${c.extracted.hta_decision_number}`}
                  </p>
                  {c.accepted_by && (
                    <p className="mt-1 text-[10px] text-cyan-800">
                      Reviewer sign-off: <b>{c.accepted_by}</b> · {String(c.accepted_at || "").slice(0, 10)}
                    </p>
                  )}
                  {c.source_document_on_file && (
                    <p className="mt-1 text-[10px] text-emerald-700">
                      Uploaded source document on file: <b>{c.source_document_on_file}</b>
                    </p>
                  )}
                  {c.conflicts?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {c.conflicts.map((cf, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 bg-amber-50 border border-amber-200 text-amber-800">
                          {cf.field}: extracted &quot;{cf.extracted}&quot; ≠ authoritative &quot;{cf.authoritative}&quot;
                        </span>
                      ))}
                    </div>
                  )}
                  {c.retraction && (
                    <p className="mt-1 text-[11px] text-rose-700">
                      <b>Retraction on file:</b> {c.retraction.reason}
                    </p>
                  )}
                  {c.match && (
                    <p className="mt-1 text-[10px] text-slate-400">
                      Matched authority: <span className="font-mono">{c.match.id || c.match.doi || "—"}</span>
                      · confidence {(c.match_confidence * 100).toFixed(0)}%
                      · on {c.matched_on?.join(", ") || "—"}
                    </p>
                  )}
                  {c.manual_overrides?.length > 0 && (
                    <p className="mt-1 text-[10px] text-slate-400 italic">
                      {c.manual_overrides.length} manual override(s) — last by {c.manual_overrides.at(-1).actor}
                    </p>
                  )}
                </div>
                {isAdmin && (
                  <Button data-testid={`citation-correct-${c.id}-btn`}
                          onClick={() => setCorrection({ citation: c, updated_fields: { ...(c.extracted || {}) }, manual_match_authority_id: c.match?.id || "", reason: "" })}
                          variant="outline" className="text-xs rounded-none shrink-0">
                    <Pen size={11} /><span className="ml-1">Correct</span>
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {correction && (
        <div data-testid="correction-dialog" className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center px-4"
             onClick={() => setCorrection(null)}>
          <div className="bg-white border border-slate-300 w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-4 py-3 bg-slate-50">
              <p className="text-sm font-bold text-slate-800">Manual search &amp; correction</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{correction.citation.filename} · current status: {STATUS_META[correction.citation.status]?.label}</p>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              {["title", "doi", "pmid", "year", "trial_registration_id", "hta_decision_number"].map((f) => (
                <div key={f}>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{f.replace("_", " ")}</label>
                  <Input data-testid={`correction-field-${f}`} value={correction.updated_fields[f] || ""}
                         onChange={(e) => setCorrection({ ...correction, updated_fields: { ...correction.updated_fields, [f]: e.target.value } })}
                         className="mt-1 text-xs" />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Manual authority match (ID)</label>
                <Input data-testid="correction-authority-id" value={correction.manual_match_authority_id}
                       onChange={(e) => setCorrection({ ...correction, manual_match_authority_id: e.target.value })}
                       placeholder="e.g. aut_1" className="mt-1 text-xs font-mono" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Reason (min 5 chars — audit-logged)</label>
                <Textarea data-testid="correction-reason" value={correction.reason}
                          onChange={(e) => setCorrection({ ...correction, reason: e.target.value })}
                          rows={3} className="mt-1 text-xs" />
              </div>
            </div>
            <div className="border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
              <Button data-testid="correction-cancel-btn" variant="ghost" onClick={() => setCorrection(null)} className="text-xs rounded-none">Cancel</Button>
              <Button data-testid="correction-submit-btn" onClick={submitCorrection}
                      className="text-xs bg-slate-900 hover:bg-slate-800 rounded-none">Apply correction</Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
